<template>
  <a v-if="product" :href="product.offerLink" class="text-none sponsored-ad-link" target="_blank" @click="registerClick">
    <v-card elevation="2" class="sponsored-ad-card card-corner-radius mt-4">
      <div class="sponsored-ad-row">
        <!-- eager: inside the absolute-positioned v-app-bar, v-img's IntersectionObserver never
             reports this element as intersecting, so without eager it never leaves isLoading and
             the image (product photo or category creative) never renders. -->
        <div class="sponsored-ad-image-wrap">
          <v-img :src="imageSrc" width="104" height="104" eager @error="onImageError"/>
          <span class="sponsored-ad-discount-tag">{{ $t('sponsoredAdMaxDiscountTag') }}</span>
        </div>
        <div class="sponsored-ad-content">
          <div class="sponsored-ad-header">
            <span class="sponsored-ad-store">{{ storeLabel }}</span>
            <span class="sponsored-ad-header-right">
              <span class="sponsored-ad-label">{{ $t('sponsoredAdLabel') }}</span>
              <v-icon size="16" color="#8A94A6">mdi-dots-horizontal</v-icon>
            </span>
          </div>
          <div class="sponsored-ad-title">{{ adTitleText }}</div>
          <div class="sponsored-ad-description">{{ adDescriptionText }}</div>
          <div class="sponsored-ad-footer">
            <span v-if="product.adBadge" class="sponsored-ad-badge">{{ product.adBadge }}</span>
            <span class="sponsored-ad-cta">
              {{ adCtaText }}
              <v-icon size="14" color="#FFFFFF">mdi-arrow-right</v-icon>
            </span>
          </div>
        </div>
      </div>
    </v-card>
  </a>
</template>

<script>
import { apiV1SponsoredAds } from "../../../service/resources/api-v1-sponsored-ads"

export default {
  name: 'SponsoredAdBanner',
  data: () => ({
    product: null,
    impressionId: null,
    imageFailed: false
  }),
  computed: {
    imageSrc: function() {
      if(this.product.imageUrl && !this.imageFailed)
        return this.product.imageUrl
      return `/images/ads/sponsored/${this.product.category}.svg`
    },
    categoryLabel: function() {
      if(!this.product || !this.product.category)
        return null
      const labelKeys = {
        acessorios: 'adCategoryAccessories',
        alimentos: 'adCategoryFood',
        beleza: 'adCategoryBeauty',
        calcados: 'adCategoryShoes',
        casa: 'adCategoryHome',
        eletronicos: 'adCategoryElectronics',
        geral: 'adCategoryGeneral',
        infantil: 'adCategoryKids',
        'moda-feminina': 'adCategoryWomensFashion',
        'moda-masculina': 'adCategoryMensFashion',
        'papelaria-arte': 'adCategoryStationeryArt',
        pet: 'adCategoryPet',
        saude: 'adCategoryHealth'
      }
      const key = labelKeys[this.product.category]
      return key ? this.$t(key) : this.product.category.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    },
    // Store name is the primary label; when a product has none (e.g. a future
    // unenriched row), fall back to its category rather than leaving it blank.
    storeLabel: function() {
      return this.product.storeName || this.categoryLabel
    },
    // Per-product ad copy, falling back to the slot's existing generic text so a
    // product with no copy of its own still renders correctly (see spec's
    // "Per-product ad copy with generic fallback" requirement).
    adTitleText: function() {
      return this.product.adTitle || this.product.name
    },
    adDescriptionText: function() {
      return this.product.adDescription || this.$t('sponsoredAdGenericDescription')
    },
    adCtaText: function() {
      return this.product.adCta || this.$t('sponsoredAdCta')
    }
  },
  mounted: function() {
    apiV1SponsoredAds.createImpression({
      userSessionId: this.$cookies.get('_migly')
    }).then(result => {
      this.product = result.data
      this.impressionId = result.data.id
      this.$emit('impression', { impressionId: this.impressionId })
    })
  },
  methods: {
    onImageError: function() {
      this.imageFailed = true
    },
    registerClick: function() {
      apiV1SponsoredAds.createClick({
        impressionId: this.impressionId,
        userSessionId: this.$cookies.get('_migly')
      })
    }
  }
}
</script>

<style scoped>
.sponsored-ad-link {
  display: block;
  text-decoration: none;
}

.sponsored-ad-card {
  overflow: hidden;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.sponsored-ad-link:hover .sponsored-ad-card {
  transform: translateY(-2px) scale(1.015);
  box-shadow: 0 12px 28px rgba(2, 119, 189, 0.2);
}

.sponsored-ad-link:active .sponsored-ad-card {
  transform: scale(0.98);
}

.sponsored-ad-row {
  display: flex;
  align-items: stretch;
  gap: 10px;
  padding: 10px;
  background: #FFFFFF;
}

.sponsored-ad-image-wrap {
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  border-radius: 10px;
  background: linear-gradient(135deg, #eef2f7, #ffffff);
}

.sponsored-ad-discount-tag {
  position: absolute;
  top: 6px;
  left: 6px;
  padding: 2px 8px;
  border-radius: 6px;
  background: #E53935;
  color: #FFFFFF;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  line-height: 1.4;
}

.sponsored-ad-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  flex: 1;
}

.sponsored-ad-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.sponsored-ad-store {
  font-size: 0.7rem;
  font-weight: 700;
  color: #5B6472;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.sponsored-ad-header-right {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.sponsored-ad-label {
  font-size: 0.62rem;
  font-weight: 600;
  color: #8A94A6;
  background: #F1F3F6;
  border-radius: 999px;
  padding: 1px 8px;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.sponsored-ad-title {
  font-size: 0.85rem;
  font-weight: 700;
  color: #101827;
  line-height: 1.25;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
}

.sponsored-ad-description {
  font-size: 0.74rem;
  color: #5B6472;
  line-height: 1.3;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.sponsored-ad-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 6px;
}

.sponsored-ad-badge {
  font-size: 0.68rem;
  font-weight: 700;
  color: #0277BD;
  background: rgba(2, 119, 189, 0.1);
  border-radius: 999px;
  padding: 2px 8px;
  margin-right: auto;
}

.sponsored-ad-cta {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  padding: 4px 10px;
  border-radius: 999px;
  background: #0277BD;
  color: #FFFFFF;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
}
</style>
